import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { DRBFMCaseInputData } from '../../../types/drbfm';
import { translateWithFallback } from '../../../utils/translation';

interface DRBFMCaseIntakeStepProps {
  isLoading: boolean;
  onSubmit: (input: DRBFMCaseInputData) => void | Promise<unknown>;
}

type RequiredField = 'title' | 'productName' | 'targetPart' | 'changeSummary';

const DEFAULT_INPUT: DRBFMCaseInputData = {
  title: 'GDS front case FPC escape review',
  productName: 'display unit',
  targetPart: 'front case',
  changeSummary: 'FPC/cable escape enlarged during manufacturing transfer',
  sourceDocumentIds: [],
  context: { drawing_page_number: 2, drawing_image_id: 'img-page-2' },
};

const readSeedParams = (): Pick<DRBFMCaseInputData, 'sourceDocumentIds' | 'context'> => {
  if (typeof window === 'undefined') {
    return {
      sourceDocumentIds: DEFAULT_INPUT.sourceDocumentIds,
      context: DEFAULT_INPUT.context,
    };
  }

  const params = new URLSearchParams(window.location.search);
  const documentId = params.get('documentId')?.trim();
  const imageId = params.get('imageId')?.trim() || DEFAULT_INPUT.context?.drawing_image_id;
  const pageNumberParam = Number(params.get('pageNumber'));
  const pageNumber = Number.isInteger(pageNumberParam) && pageNumberParam > 0
    ? pageNumberParam
    : DEFAULT_INPUT.context?.drawing_page_number;

  return {
    sourceDocumentIds: documentId ? [documentId] : DEFAULT_INPUT.sourceDocumentIds,
    context: {
      ...DEFAULT_INPUT.context,
      drawing_page_number: pageNumber,
      drawing_image_id: imageId,
    },
  };
};

const buildInitialInput = (): DRBFMCaseInputData => ({
  ...DEFAULT_INPUT,
  ...readSeedParams(),
});

const shortId = (value?: string) => {
  if (!value) return '';
  return value.length > 16 ? `${value.slice(0, 8)}...${value.slice(-4)}` : value;
};

const requiredFields: RequiredField[] = [
  'title',
  'productName',
  'targetPart',
  'changeSummary',
];

const DRBFMCaseIntakeStep: React.FC<DRBFMCaseIntakeStepProps> = ({
  isLoading,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [input, setInput] = useState<DRBFMCaseInputData>(buildInitialInput);
  const [errors, setErrors] = useState<Partial<Record<RequiredField, string>>>({});
  const drawingPageNumberValue = input.context?.drawing_page_number;
  const drawingPageNumber = typeof drawingPageNumberValue === 'number' || typeof drawingPageNumberValue === 'string'
    ? drawingPageNumberValue
    : 2;
  const drawingImageId = String(input.context?.drawing_image_id || 'img-page-2');
  const sourceDocumentId = input.sourceDocumentIds?.[0];
  const fieldLabels: Record<RequiredField, string> = {
    title: t('drbfm.case.fields.title', 'ケースタイトル'),
    productName: t('drbfm.case.fields.productName', '製品'),
    targetPart: t('drbfm.case.fields.targetPart', '対象部品'),
    changeSummary: t('drbfm.case.fields.changeSummary', '変更概要'),
  };
  const updateField = (field: RequiredField, value: string) => {
    setInput((current) => ({ ...current, [field]: value }));
    if (value.trim()) {
      setErrors((current) => ({ ...current, [field]: undefined }));
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: Partial<Record<RequiredField, string>> = {};
    requiredFields.forEach((field) => {
      if (!input[field].trim()) {
        nextErrors[field] = translateWithFallback(t, 'drbfm.case.required', `${fieldLabels[field]}は必須です`, {
          field: fieldLabels[field],
        });
      }
    });

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    await onSubmit({
      ...input,
      title: input.title.trim(),
      productName: input.productName.trim(),
      targetPart: input.targetPart.trim(),
      changeSummary: input.changeSummary.trim(),
      sourceDocumentIds: input.sourceDocumentIds || [],
      context: input.context || {},
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h6" component="h2" gutterBottom>
            {t('drbfm.case.title', 'GDSケース作成')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('drbfm.case.description', '図面ページとGDS移管時の変更概要を起点にDRBFMケースを作成します。')}
          </Typography>
        </Box>

        <Alert severity="info">
          {translateWithFallback(t, 'drbfm.case.referenceDrawing', `参照図面: ${drawingPageNumber}ページ目`, {
            page: drawingPageNumber,
          })}
          {sourceDocumentId ? ` / ${translateWithFallback(t, 'drbfm.case.documentId', `文書ID ${shortId(sourceDocumentId)}`, {
            id: shortId(sourceDocumentId),
          })}` : ''}
          {' / '}
          {translateWithFallback(t, 'drbfm.case.imageId', `画像ID ${shortId(drawingImageId)}`, {
            id: shortId(drawingImageId),
          })}
        </Alert>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label={fieldLabels.title}
            value={input.title}
            onChange={(event) => updateField('title', event.target.value)}
            error={Boolean(errors.title)}
            helperText={errors.title}
            disabled={isLoading}
            fullWidth
            required
            size="small"
          />
          <TextField
            label={fieldLabels.productName}
            value={input.productName}
            onChange={(event) => updateField('productName', event.target.value)}
            error={Boolean(errors.productName)}
            helperText={errors.productName}
            disabled={isLoading}
            fullWidth
            required
            size="small"
          />
        </Stack>

        <TextField
          label={fieldLabels.targetPart}
          value={input.targetPart}
          onChange={(event) => updateField('targetPart', event.target.value)}
          error={Boolean(errors.targetPart)}
          helperText={errors.targetPart}
          disabled={isLoading}
          fullWidth
          required
          size="small"
        />

        <TextField
          label={fieldLabels.changeSummary}
          value={input.changeSummary}
          onChange={(event) => updateField('changeSummary', event.target.value)}
          error={Boolean(errors.changeSummary)}
          helperText={errors.changeSummary}
          disabled={isLoading}
          fullWidth
          required
          multiline
          minRows={4}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            disabled={isLoading}
          >
            {isLoading
              ? t('drbfm.case.extractingChanges', '変更点を抽出中')
              : t('drbfm.case.extractChanges', '変更点を抽出')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default DRBFMCaseIntakeStep;
